import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type DependencyList,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button, Chip, FieldMessage, Spinner, TextField, Tooltip, fieldDescription } from '../ui';
import { ApiError, IdempotentAction, isRetryable, translateProblem } from '../api';
import { ct, extraTones, locale, tOr, useConsoleI18n, type Tone } from './bind';

// Page building blocks in the console's language (nyk-* classes from @nyce/ui/console.css), shared by the office and
// the client portal. Strings come from the app's catalogue (see `bindConsole`).

/** Page header: title, one-line description, filters/primary action on the right. */
export function Page({
  title,
  description,
  actions,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="nyk-page">
      <div className="nyk-page-head">
        <div className="nyk-page-title">
          <h1>{title}</h1>
          {description && <p className="nyk-page-desc">{description}</p>}
        </div>
        {actions && <div className="nyk-actions">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/** A content card with an optional heading row. */
export function Card({
  title,
  hint,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={['nyk-card', className].filter(Boolean).join(' ')}>
      {(title || actions) && (
        <div className="nyk-card-head">
          <div className="nyk-card-title-group">
            {title && <h2>{title}</h2>}
            {hint && <span className="nyk-ui-hint">{hint}</span>}
          </div>
          {actions && <div className="nyk-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** Below this width every list table is shown as cards. */
export const CARDS_QUERY = '(max-width: 720px)';

/**
 * Every table lives in one of these so the page body never scrolls sideways. A list table (`nyk-cards`) is shown as
 * cards (`nyk-as-cards`) on a phone and wherever it does not fit even with its text wrapped (many columns on a narrow
 * window or in a dialog), so lists never scroll sideways either; it turns back into a table once there is room.
 */
export function TableScroller({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const phone = typeof window.matchMedia === 'function' ? window.matchMedia(CARDS_QUERY) : null;
    const check = () => {
      if (!el.querySelector(':scope > table.nyk-cards')) return el.classList.remove('nyk-as-cards');
      if (phone?.matches) return el.classList.add('nyk-as-cards');
      // measured as a table: cards only when the table is wider than its box
      el.classList.remove('nyk-as-cards');
      if (el.scrollWidth > el.clientWidth + 1) el.classList.add('nyk-as-cards');
    };
    check();
    // after the resize, not inside the observer (switching changes the size it reports)
    let frame = 0;
    const later = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(check);
    };
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(later) : null;
    resize?.observe(el);
    // rows arrive or change without the box changing width
    const rows = new MutationObserver(later);
    rows.observe(el, { childList: true, subtree: true, characterData: true });
    phone?.addEventListener?.('change', check);
    return () => {
      cancelAnimationFrame(frame);
      resize?.disconnect();
      rows.disconnect();
      phone?.removeEventListener?.('change', check);
    };
  }, []);
  return (
    <div ref={ref} className={['nyk-scroll', className].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}

/**
 * A row's buttons on their own line under it, for rows with more actions than fit beside the data (no actions
 * column, so the table stays narrow). Put the data row and this one in a `<tbody className="nyk-row-group">` per item.
 */
export function ActionsRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr className="nyk-actions-line">
      <td colSpan={colSpan}>
        <div className="nyk-row-actions">{children}</div>
      </td>
    </tr>
  );
}

/** Empty list: what is missing and, when the caller may act, the one thing to do next (`card`: on its own card). */
export function EmptyState({
  title,
  hint,
  action,
  card,
}: {
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  card?: boolean;
}) {
  return (
    <div className={`nyk-empty nyk-ui-empty-state${card ? ' nyk-card' : ''}`}>
      <p className="nyk-empty-title">{title}</p>
      {hint && <p className="nyk-empty-hint">{hint}</p>}
      {action && <div className="nyk-empty-action">{action}</div>}
    </div>
  );
}

export function Loading({ loading }: { loading: boolean }) {
  return loading ? (
    <div className="nyk-loading">
      <Spinner size={18} />
    </div>
  ) : null;
}

/** Human text for any error: problem+json title/detail/errors, network failures, the rest. */
export function errorText(e: unknown): { title: string; detail?: string; fields: string[] } {
  if (e instanceof ApiError) {
    const b = (e.body ?? {}) as {
      title?: string;
      detail?: string;
      errors?: Record<string, string[] | string> | string[];
    };
    // Validation errors come per field (ASP.NET) or as a plain list (Products: every problem of an item).
    const list = Array.isArray(b.errors);
    const fields = !b.errors
      ? []
      : Array.isArray(b.errors)
        ? b.errors
        : Object.entries(b.errors).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
    // By the problem's stable code in the app's language where its catalogue has it (ADR 0031).
    const translated = translateProblem(e);
    const title = translated?.title ?? b.title ?? e.message;
    const detail = translated ? translated.detail : b.detail;
    return { title, detail: detail && detail !== title && !list ? detail : undefined, fields };
  }
  if (e instanceof TypeError)
    return { title: ct('error.noConnection'), detail: ct('error.unreachable'), fields: [] };
  return { title: e instanceof Error ? e.message : ct('error.generic'), fields: [] };
}

/**
 * Inline error (problem+json aware: title, detail, field errors) with an optional retry; a plain string is shown as it
 * is. `retry="retryable"` offers the retry only for network errors and 5xx (a 400 will not get better). `onShow` runs
 * once per error (a sound, a vibration).
 */
export function ErrorBox({
  error,
  onRetry,
  retry = 'always',
  onShow,
}: {
  error: unknown;
  onRetry?: () => void;
  retry?: 'always' | 'retryable';
  onShow?: (error: unknown) => void;
}) {
  const { t } = useConsoleI18n();
  const showRef = useRef(onShow);
  showRef.current = onShow;
  useEffect(() => {
    if (error) showRef.current?.(error);
  }, [error]);
  if (!error) return null;
  const canRetry = !!onRetry && (retry === 'always' || isRetryable(error));
  if (typeof error === 'string' && !canRetry)
    return (
      <div className="nyk-ui-login-error nyk-ui-connection" role="alert">
        {error}
      </div>
    );
  const { title, detail, fields } = errorText(error);
  return (
    <div className="nyk-ui-login-error nyk-ui-connection nyk-error" role="alert">
      <div>
        <b>{title}</b>
        {detail && <div>{detail}</div>}
        {fields.length > 0 && (
          <ul>
            {fields.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}
      </div>
      {canRetry && (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          {t('common.tryAgain')}
        </Button>
      )}
    </div>
  );
}

export function Kpi({
  label,
  value,
  sub,
  help,
  tone,
  delta,
  to,
  Link,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  /** One or two sentences behind the (i) button: what the number is and what it is not. */
  help?: string;
  tone?: Tone;
  /** Change versus the previous period, shown next to the value. */
  delta?: ReactNode;
  /** Makes the tile a link (an overview → its detail page), through `Link` (the router's) or a plain anchor. */
  to?: string;
  Link?: ComponentType<{ to: string; className?: string; children?: ReactNode }>;
}) {
  const { t } = useConsoleI18n();
  const cls = ['nyk-kpi', tone ? `nyk-kpi-${tone}` : '', to ? 'nyk-kpi-link' : ''].filter(Boolean).join(' ');
  const body = (
    <>
      <div className="nyk-kpi-head">
        <span className="nyk-ui-eyebrow">{label}</span>
        {help && (
          <Tooltip text={help}>
            <button type="button" className="nyk-kpi-help" aria-label={t('common.about', { label })}>
              i
            </button>
          </Tooltip>
        )}
      </div>
      <div className="nyk-kpi-row">
        <div className="nyk-kpi-value">{value}</div>
        {delta}
      </div>
      {sub && <div className="nyk-kpi-sub">{sub}</div>}
    </>
  );
  if (!to)
    return (
      <div className={cls} data-kpi={label}>
        {body}
      </div>
    );
  return Link ? (
    <Link to={to} className={cls}>
      {body}
    </Link>
  ) : (
    <a href={to} className={cls} data-kpi={label}>
      {body}
    </a>
  );
}

const tabId = (tabs: string, value: string) => `${tabs}-tab-${value.replace(/\s+/g, '_')}`;
const panelId = (tabs: string) => `${tabs}-panel`;

/**
 * Segment tabs (WAI-ARIA tabs pattern): one tab stop, arrow keys / Home / End move to and select the next enabled tab,
 * a disabled tab is skipped. With `id` each tab controls the `TabPanel` of the same id:
 *
 * ```tsx
 * const tabs = useId();
 * <Tabs id={tabs} value={tab} onChange={setTab} options={TABS} aria-label="Tenant" />
 * <TabPanel tabs={tabs} value={tab}>…</TabPanel>
 * ```
 *
 * Without `id` it is a tab-styled switch over the rest of the page (a time range, an environment).
 */
export function Tabs<T extends string>({
  value,
  onChange,
  options,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; disabled?: boolean }[];
  /** Links the tabs to their `TabPanel` (same id). */
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}) {
  const refs = useRef(new Map<T, HTMLButtonElement>());
  // The tab stop: the selected tab, or the first enabled one when the selection is not (or no longer) a tab here.
  const stop =
    options.find((o) => o.value === value && !o.disabled)?.value ?? options.find((o) => !o.disabled)?.value;
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, current: T) => {
    const enabled = options.filter((o) => !o.disabled);
    const at = enabled.findIndex((o) => o.value === current);
    const next =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? enabled[(at + 1) % enabled.length]
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? enabled[(at - 1 + enabled.length) % enabled.length]
          : e.key === 'Home'
            ? enabled[0]
            : e.key === 'End'
              ? enabled[enabled.length - 1]
              : undefined;
    if (!next) return;
    e.preventDefault();
    refs.current.get(next.value)?.focus();
    if (next.value !== value) onChange(next.value);
  };
  return (
    <div
      className="nyk-ui-segment nyk-tabs"
      role="tablist"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {options.map((o) => (
        <button
          key={o.value}
          ref={(el) => {
            if (el) refs.current.set(o.value, el);
            else refs.current.delete(o.value);
          }}
          type="button"
          role="tab"
          id={id ? tabId(id, o.value) : undefined}
          aria-controls={id && value === o.value ? panelId(id) : undefined}
          aria-selected={value === o.value}
          tabIndex={o.value === stop ? 0 : -1}
          disabled={o.disabled}
          className={`nyk-ui-segment-btn${value === o.value ? ' nyk-ui-selected' : ''}`}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => onKeyDown(e, o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** The content of the selected tab of `Tabs` with the same `id` (`tabs`): named by that tab, one tab stop. */
export function TabPanel({
  tabs,
  value,
  className,
  children,
}: {
  tabs: string;
  value: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={panelId(tabs)}
      aria-labelledby={tabId(tabs, value)}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}

/** Status words across the services, mapped to a tone. Unknown statuses are grey; apps add their own (`bindConsole`). */
const TONES: Record<string, Tone> = {
  // in flight
  receiving: 'blue',
  assigned: 'blue',
  released: 'blue',
  started: 'blue',
  sent: 'blue',
  packing: 'blue',
  arrived: 'blue',
  atDoor: 'blue',
  pending: 'blue',
  queued: 'blue',
  open: 'blue',
  booked: 'blue',
  ready: 'blue',
  allocated: 'blue',
  picked: 'blue',
  packed: 'blue',
  inTransit: 'blue',
  received: 'grey',
  // done
  closed: 'green',
  completed: 'green',
  shipped: 'green',
  dispatched: 'green',
  departed: 'green',
  processed: 'green',
  delivered: 'green',
  available: 'green',
  // needs attention
  onHold: 'amber',
  partiallyAllocated: 'amber',
  short: 'amber',
  damaged: 'amber',
  quarantine: 'amber',
  failed: 'red',
  deadLettered: 'red',
  blocked: 'red',
  cancelled: 'grey',
};

export const toneOf = (status: string): Tone => extraTones()[status] ?? TONES[status] ?? 'grey';

/** Display label of a status code: `status.<code>` from the catalogue, else the code humanized. */
export const statusLabel = (value: string) => tOr(`status.${value}`, humanize(value));

/** A status as a small chip (Chip + console tone). */
/** A shape next to the colour (WCAG 1.4.1): what the tone means is never colour alone. */
const TONE_MARK: Partial<Record<Tone, ReactNode>> = {
  red: <AlertCircle size={12} aria-hidden="true" className="nyk-tone-mark" />,
  amber: <AlertTriangle size={12} aria-hidden="true" className="nyk-tone-mark" />,
  green: <CheckCircle2 size={12} aria-hidden="true" className="nyk-tone-mark" />,
};

export function StatusChip({
  value,
  tone,
  label,
  icon,
  className,
  'aria-label': ariaLabel,
}: {
  value: string;
  tone?: Tone;
  /**
   * What a screen reader says instead of the visible text ("Payment overdue" for a chip showing "Overdue"). Read as
   * hidden text: a plain span cannot carry `aria-label`.
   */
  'aria-label'?: string;
  /** The text (default: `status.<value>` from the catalogue, else the value humanized). */
  label?: ReactNode;
  /** The shape next to the colour (default by tone). */
  icon?: ReactNode;
  className?: string;
}) {
  useConsoleI18n();
  const t = tone ?? toneOf(value);
  return (
    <Chip
      size="sm"
      className={[`nyk-tone-${t}`, className].filter(Boolean).join(' ')}
      icon={icon ?? TONE_MARK[t]}
      data-status={value}
    >
      {ariaLabel ? (
        <>
          <span aria-hidden="true">{label ?? statusLabel(value)}</span>
          <span className="nyk-ui-sr-only">{ariaLabel}</span>
        </>
      ) : (
        (label ?? statusLabel(value))
      )}
    </Chip>
  );
}

/**
 * One kind of command from a screen: tracks busy/error and the idempotency key (reused when an identical payload is
 * retried after a network error or 5xx).
 */
export function useCommand() {
  const action = useMemo(() => new IdempotentAction(), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = useCallback(
    async <T,>(payload: unknown, fn: (key: string) => Promise<T>): Promise<T | undefined> => {
      setBusy(true);
      setError(null);
      try {
        return await action.run(payload, fn);
      } catch (e) {
        setError(e);
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [action]
  );
  const clearError = useCallback(() => setError(null), []);
  return { run, busy, error, clearError };
}

/** `value` once it has stopped changing for `ms` (a search box that queries the server). */
export function useDebounced<T>(value: T, ms = 300): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** "Load more" under a keyset-paged list (`usePaged`): the button while there is a next page, and its error. */
export function LoadMore({
  list,
}: {
  list: { next: string | null; loadingMore: boolean; moreError: unknown; loadMore: () => Promise<void> };
}) {
  const { t } = useConsoleI18n();
  return (
    <>
      <ErrorBox error={list.moreError} onRetry={() => void list.loadMore()} />
      {list.next && (
        <div className="nyk-pager">
          <Button variant="ghost" size="sm" loading={list.loadingMore} onClick={() => void list.loadMore()}>
            {t('common.loadMore')}
          </Button>
        </div>
      )}
    </>
  );
}

/** value / max as a thin bar with the numbers. */
export function Progress({ value, max }: { value: number; max: number }) {
  const { t } = useConsoleI18n();
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <span
      className="nyk-progress"
      role="img"
      aria-label={t('common.ofTotal', { value: num(value), max: num(max) })}
    >
      <span className="nyk-progress-track">
        <span
          className={`nyk-progress-fill${max > 0 && value > max ? ' nyk-progress-fill-over' : ''}`}
          style={{ width: `${pct}%`, display: 'block' }}
        />
      </span>
      <span className="nyk-progress-text">
        {num(value)} / {num(max)}
      </span>
    </span>
  );
}

/** A key/value table (nyk-kv) of the rows that have a value. */
export function KeyValues({ rows }: { rows: [ReactNode, ReactNode | null | undefined][] }) {
  return (
    <table className="nyk-kv">
      <tbody>
        {rows
          .filter(([, v]) => v !== null && v !== undefined && v !== '')
          .map(([k, v], i) => (
            <tr key={i}>
              <th>{k}</th>
              <td>{v}</td>
            </tr>
          ))}
      </tbody>
    </table>
  );
}

// ---------- from the Operations Hub ----------

/** Minimal async loader: `{data, loading, error, reload}`; `deps` re-run the loader. */
export function useAsync<T>(fn: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const reload = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    try {
      const d = await fn();
      if (id === seq.current) {
        setData(d);
        setError(null);
      }
    } catch (e) {
      if (id === seq.current) setError((e as Error).message);
    } finally {
      if (id === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, loading, error, reload, setData };
}

/** A button on a notice ("Reload"); clicking it runs `onClick` and dismisses the notice. */
export interface NoticeAction {
  label: string;
  onClick: () => void;
}

export interface Notice {
  text: string;
  /** `warning`: a conflict or a changed record, not a failure; stays like `error`. */
  kind: 'success' | 'error' | 'warning' | 'info';
  action?: NoticeAction;
}

/**
 * Page-level notices shown as a toast at the bottom of the viewport: successes fade after a few seconds, errors,
 * warnings and info (a new version is out) stay until dismissed or replaced; any of them may carry an `action`. Rendered
 * by the page itself (no provider), so it works in dialogs and tests alike. `element` is always rendered: its live
 * regions stay mounted, so a notice is announced when its text arrives.
 */
export function useNotice(duration = 6000) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setNotice(null);
  }, []);
  const show = useCallback(
    (text: string, kind: Notice['kind'], action?: NoticeAction) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      setNotice({ text, kind, action });
      if (kind === 'success') timer.current = setTimeout(() => setNotice(null), duration);
    },
    [duration]
  );
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  const success = useCallback((text: string, action?: NoticeAction) => show(text, 'success', action), [show]);
  const error = useCallback((text: string, action?: NoticeAction) => show(text, 'error', action), [show]);
  const warning = useCallback((text: string, action?: NoticeAction) => show(text, 'warning', action), [show]);
  const info = useCallback((text: string, action?: NoticeAction) => show(text, 'info', action), [show]);
  const element = <NoticeToast notice={notice} onClose={clear} />;
  return { notice, success, error, warning, info, clear, element };
}

/**
 * The toast of a `Notice`. Both live regions are always mounted (a region mounted together with its text may not be
 * announced) and the text is set into one of them: `error` and `warning` into the `alert` region, `success` and `info`
 * into the polite `status` one. With `notice` null nothing shows.
 */
export function NoticeToast({ notice, onClose }: { notice: Notice | null; onClose: () => void }) {
  const { t } = useConsoleI18n();
  const urgent = notice?.kind === 'error' || notice?.kind === 'warning';
  const toast = notice && (
    <div className={`nyk-toast nyk-toast-${notice.kind}`} data-toast={notice.kind}>
      <span className="nyk-toast-text">{notice.text}</span>
      {notice.action && (
        <button
          type="button"
          className="nyk-toast-action"
          onClick={() => {
            notice.action!.onClick();
            onClose();
          }}
        >
          {notice.action.label}
        </button>
      )}
      <button type="button" className="nyk-toast-close" aria-label={t('common.dismiss')} onClick={onClose}>
        ×
      </button>
    </div>
  );
  return (
    <div className="nyk-toasts" data-nyk-live="">
      <div className="nyk-toasts-region" role="status" aria-live="polite">
        {!urgent && toast}
      </div>
      <div className="nyk-toasts-region" role="alert">
        {urgent && toast}
      </div>
    </div>
  );
}

/** Chips with a remove button plus an input: Enter, comma or blur adds; used for space keys and share ids. */
export function TagsField({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  hint,
}: {
  label: ReactNode;
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  const { t } = useConsoleI18n();
  const ids = fieldDescription(useId(), hint, undefined);
  const [draft, setDraft] = useState('');
  const commit = () => {
    const add = textToList(draft).filter((x) => !value.includes(x));
    if (add.length) onChange([...value, ...add]);
    setDraft('');
  };
  return (
    <div className="nyk-ui-field nyk-tags" data-tags={typeof label === 'string' ? label : undefined}>
      <span>{label}</span>
      <div className="nyk-tags-box">
        {value.map((v) => (
          <span key={v} className="nyk-ui-chip nyk-ui-chip-sm nyk-tag">
            {v}
            {!disabled && (
              <button
                type="button"
                className="nyk-tag-remove"
                aria-label={t('common.removeValue', { value: v })}
                onClick={() => onChange(value.filter((x) => x !== v))}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          className="nyk-ui-input nyk-tags-input"
          aria-label={typeof label === 'string' ? label : undefined}
          aria-describedby={ids.describedBy}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
          }}
        />
      </div>
      <FieldMessage hint={hint} ids={ids} />
    </div>
  );
}

export const listToText = (v: string[] | undefined) => (v ?? []).join(', ');
export const textToList = (s: string) =>
  s
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter(Boolean);

export interface ListFieldProps {
  label: string;
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  disabled?: boolean;
  hint?: ReactNode;
  placeholder?: string;
}

/**
 * A comma-separated list of values, typed as text.
 *
 * It holds the raw text while you type and only parses on the way out. Binding the input straight to
 * `listToText(textToList(...))` cannot work: `textToList` drops empty segments, so the comma you just pressed is parsed
 * away and re-joined out of existence before the next keystroke — which made every one of these fields silently
 * single-valued. The list still parses on every change, so the form sees the values without waiting for a blur.
 */
export function ListField({ label, value, onChange, disabled, hint, placeholder }: ListFieldProps) {
  const [text, setText] = useState(() => listToText(value));
  // Follow the record when it is replaced underneath us (a different row, or a reset), but never while typing:
  // comparing the parsed list, not the text, is what keeps "a, " from being rewritten to "a".
  const shown = useRef(value ?? []);
  useEffect(() => {
    const next = value ?? [];
    if (listToText(next) !== listToText(shown.current)) {
      shown.current = next;
      setText(listToText(next));
    }
  }, [value]);

  return (
    <TextField
      label={label}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = textToList(e.target.value);
        shown.current = parsed;
        onChange(parsed);
      }}
      disabled={disabled}
      hint={hint}
      placeholder={placeholder}
    />
  );
}

/** Re-run `fn` every `ms` while `on`; paused while the tab is hidden. */
export function useInterval(fn: () => void, ms: number, on: boolean) {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  useEffect(() => {
    if (!on) return;
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') ref.current();
    }, ms);
    return () => clearInterval(id);
  }, [ms, on]);
}

/** "1–25 of 123" with Previous / Next for the paged list endpoints. */
export function Pager({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t } = useConsoleI18n();
  if (total <= pageSize && page === 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const last = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="nyk-pager" data-testid="pager">
      <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        {t('pager.previous')}
      </Button>
      <span className="nyk-pager-text">
        {total ? t('pager.range', { from, to, total }) : t('pager.none')}
      </span>
      <Button size="sm" variant="ghost" disabled={page >= last} onClick={() => onPage(page + 1)}>
        {t('pager.next')}
      </Button>
    </div>
  );
}

/** Monospace id with a copy affordance. */
export function Code({ children, title }: { children: string; title?: string }) {
  return (
    <code className="nyk-code" title={title ?? children}>
      {children}
    </code>
  );
}

// ---------- formatting (in the UI language's locale) ----------

/** "partiallyAllocated" → "Partially allocated". */
export function humanize(value: string): string {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function num(n: number | null | undefined, digits = 3): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '–';
  return n.toLocaleString(locale(), { maximumFractionDigits: digits });
}

/** A `YYYY-MM-DD` day in the UI language's short date format (no time zone shift). */
export function day(value: string | null | undefined): string {
  if (!value) return '–';
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(locale(), {
    dateStyle: 'short',
    timeZone: 'UTC',
  });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '–'
    : d.toLocaleString(locale(), { dateStyle: 'short', timeStyle: 'short' });
}

export function timeAgo(iso: string | number | null | undefined): string {
  if (!iso) return '';
  const t = typeof iso === 'number' ? iso : Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 45) return ct('time.justNow');
  if (s < 3600) return ct('time.minAgo', { n: Math.round(s / 60) });
  if (s < 86400) return ct('time.hAgo', { n: Math.round(s / 3600) });
  return new Date(t).toLocaleDateString(locale());
}

export function money(amount: number, currency: string): string {
  try {
    return amount.toLocaleString(locale(), { style: 'currency', currency });
  } catch {
    return `${num(amount)} ${currency}`;
  }
}

/** yyyy-mm-dd of today (UTC, as the services count days). */
export function todayUtc(offsetDays = 0): string {
  return new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 10);
}

// ---------- sizes, durations, counts (unit words, no language) ----------

const UNITS = ['B', 'kB', 'MB', 'GB', 'TB', 'PB'];

/** `1536` → `1.5 kB` (1024-based, like `pg_size_pretty`); null/undefined → `—`. */
export function formatBytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const neg = n < 0;
  let v = Math.abs(n);
  let u = 0;
  while (v >= 1024 && u < UNITS.length - 1) {
    v /= 1024;
    u++;
  }
  const digits = u === 0 || v >= 100 ? 0 : v >= 10 ? 1 : 2;
  const text = `${Number(v.toFixed(digits))} ${UNITS[u]}`;
  return neg ? `-${text}` : text;
}

/** Compact duration: `850 ms`, `12 s`, `3 min 20 s`, `2 h 5 min`, `3 d 4 h`. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 0) ms = 0;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m} min ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

const toMs = (t: string | number | Date) => (t instanceof Date ? t.getTime() : new Date(t).getTime());

/** Milliseconds between two instants (`to` defaults to now); null when `from` is missing. */
export function elapsedMs(
  from: string | null | undefined,
  to?: string | null,
  now: number = Date.now()
): number | null {
  if (!from) return null;
  const a = toMs(from);
  const b = to ? toMs(to) : now;
  return Number.isFinite(a) && Number.isFinite(b) ? Math.max(0, b - a) : null;
}

/** `1234567` → `1.2 M`, `53200` → `53.2 k` (row estimates, xid ages). */
export function formatCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const f = (v: number, s: string) => `${Number(v.toFixed(v >= 100 ? 0 : 1))} ${s}`;
  if (abs >= 1e9) return f(n / 1e9, 'G');
  if (abs >= 1e6) return f(n / 1e6, 'M');
  if (abs >= 1e4) return f(n / 1e3, 'k');
  return Math.round(n).toLocaleString();
}
